import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { 
  Flame, 
  TrendingUp, 
  TrendingDown, 
  AlertTriangle, 
  Sparkles, 
  Target,
  BarChart3,
  Zap,
  RefreshCw,
  ExternalLink,
  Skull,
  Clock
} from 'lucide-react';
import { useLowCapOpportunities, useLowCapMarketAnalysis, useRunLowCapAnalysis, type LowCapToken } from '@/hooks/useLowCapOpportunities';
import { useToast } from '@/components/ui/use-toast';
import DisclaimerNote from '@/components/DisclaimerNote';
import { format, formatDistanceToNow, differenceInHours } from 'date-fns';

const formatPrice = (price: number) => {
  if (price < 0.00001) return `A$${price.toFixed(8)}`;
  if (price < 0.01) return `A$${price.toFixed(6)}`;
  if (price < 1) return `A$${price.toFixed(4)}`;
  return `A$${price.toFixed(2)}`;
};

const formatMarketCap = (mcap: number) => {
  if (mcap >= 1000000) return `A$${(mcap / 1000000).toFixed(2)}M`;
  if (mcap >= 1000) return `A$${(mcap / 1000).toFixed(0)}K`;
  return `A$${mcap.toFixed(0)}`;
};

const getRiskColor = (risk: number | null) => {
  if (!risk) return 'bg-muted text-muted-foreground';
  if (risk >= 90) return 'bg-red-600 text-white';
  if (risk >= 80) return 'bg-red-500 text-white';
  if (risk >= 70) return 'bg-orange-500 text-white';
  return 'bg-yellow-500 text-black';
};

const getNarrativeColor = (narrative: string | null) => {
  switch (narrative) {
    case 'AI/ML': return 'bg-purple-500/20 text-purple-400 border-purple-500/30';
    case 'Meme': return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
    case 'Gaming/Metaverse': return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
    case 'DeFi': return 'bg-green-500/20 text-green-400 border-green-500/30';
    case 'Infrastructure': return 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30';
    default: return 'bg-muted text-muted-foreground border-border';
  }
};

const TokenCard = ({ token, rank }: { token: LowCapToken; rank: number }) => {
  const priceChange = token.price_change_24h || 0;
  const isPositive = priceChange >= 0;

  return (
    <Card className="bg-card/50 border-red-500/20 hover:border-red-500/40 transition-all">
      <CardContent className="p-4">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-3">
            <div className="flex items-center justify-center w-8 h-8 rounded-full bg-gradient-to-br from-red-500 to-orange-500 text-white font-bold text-sm">
              {rank}
            </div>
            <div>
              <h4 className="font-bold text-foreground">{token.token_symbol}</h4>
              <p className="text-xs text-muted-foreground truncate max-w-[120px]">{token.token_name}</p>
            </div>
          </div>
          <div className="text-right">
            <p className="font-mono text-sm font-semibold text-foreground">{formatPrice(token.current_price)}</p>
            <div className={`flex items-center gap-1 text-xs ${isPositive ? 'text-green-400' : 'text-red-400'}`}>
              {isPositive ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
              {priceChange.toFixed(1)}%
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2 mb-3 flex-wrap">
          <Badge variant="outline" className="text-xs bg-red-500/10 border-red-500/30 text-red-400">
            {formatMarketCap(token.market_cap)}
          </Badge>
          <Badge variant="outline" className={`text-xs ${getNarrativeColor(token.narrative)}`}>
            {token.narrative || 'Unknown'}
          </Badge>
          <Badge className={`text-xs ${getRiskColor(token.risk_score)}`}>
            <Skull className="w-3 h-3 mr-1" />
            {token.risk_score || 70}% Risk
          </Badge>
        </div>

        <div className="flex items-center gap-2 mb-3">
          <div className="flex-1 bg-muted rounded-full h-2 overflow-hidden">
            <div 
              className="h-full bg-gradient-to-r from-orange-500 to-red-500 transition-all"
              style={{ width: `${token.opportunity_score}%` }}
            />
          </div>
          <span className="text-xs font-bold text-orange-400">{token.opportunity_score}/100</span>
        </div>

        {token.buy_signals && token.buy_signals.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-3">
            {token.buy_signals.slice(0, 3).map((signal, idx) => (
              <Badge 
                key={idx} 
                variant="outline" 
                className={`text-[10px] ${
                  signal.strength === 'STRONG' 
                    ? 'bg-green-500/20 text-green-400 border-green-500/30' 
                    : 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30'
                }`}
              >
                <Zap className="w-2 h-2 mr-1" />
                {signal.description}
              </Badge>
            ))}
          </div>
        )}

        {token.ai_analysis && (
          <div className="bg-muted/50 rounded-lg p-2 mb-3">
            <div className="flex items-center gap-1 text-xs text-muted-foreground mb-1">
              <Sparkles className="w-3 h-3 text-purple-400" />
              AI Take
            </div>
            <p className="text-xs text-foreground/80 line-clamp-2">{token.ai_analysis}</p>
          </div>
        )}

        <div className="flex gap-2">
          <a
            href={`https://www.binance.com/en-AU/trade/${token.token_symbol}_USDT?ref=GRO_28502_5AZZR`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex-1"
          >
            <Button variant="default" size="sm" className="w-full text-xs bg-gradient-to-r from-yellow-500 to-amber-600 hover:opacity-90 text-black font-semibold">
              Buy on Binance
            </Button>
          </a>
          <a
            href={`https://www.coingecko.com/en/coins/${token.token_id}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            <Button variant="outline" size="sm" className="text-xs">
              <ExternalLink className="w-3 h-3" />
            </Button>
          </a>
        </div>
      </CardContent>
    </Card>
  );
};

const LowCapGemsWidget = () => {
  const { toast } = useToast();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const { data: tokens, isLoading: tokensLoading, refetch: refetchTokens } = useLowCapOpportunities(5);
  const { data: analysis, isLoading: analysisLoading, refetch: refetchAnalysis } = useLowCapMarketAnalysis();
  const { runAnalysis } = useRunLowCapAnalysis();

  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await runAnalysis();
      await Promise.all([refetchTokens(), refetchAnalysis()]);
      toast({
        title: "Analysis Updated",
        description: "Low-cap gems have been refreshed with latest data",
      });
    } catch (error) {
      toast({
        title: "Refresh Failed",
        description: "Could not update low-cap analysis. Try again later.",
        variant: "destructive",
      });
    } finally {
      setIsRefreshing(false);
    }
  };

  const isLoading = tokensLoading || analysisLoading;

  // LIVE-ONLY GATE: hide widget entirely unless analysis was generated within the
  // freshness window. Gives one cron cycle of grace before the widget disappears.
  // No stale fallback, no skeleton-forever — if cron keeps failing, the widget hides.
  const FRESHNESS_HOURS = 36;
  const ageHours = analysis?.created_at
    ? differenceInHours(new Date(), new Date(analysis.created_at))
    : Infinity;
  const isLive =
    analysis?.created_at != null &&
    ageHours <= FRESHNESS_HOURS &&
    (tokens?.length ?? 0) > 0;

  if (!isLoading && !isLive) {
    return null;
  }

  if (isLoading) {
    return (
      <Card className="border-red-500/30 bg-gradient-to-br from-red-950/20 to-orange-950/20">
        <CardHeader>
          <Skeleton className="h-8 w-64" />
        </CardHeader>
        <CardContent className="space-y-4">
          <Skeleton className="h-32 w-full" />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[...Array(5)].map((_, i) => (
              <Skeleton key={i} className="h-48 w-full" />
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-red-500/30 bg-gradient-to-br from-red-950/20 to-orange-950/20 overflow-hidden">
      <CardHeader className="border-b border-red-500/20">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-3">
            <div className="relative">
              <Flame className="w-8 h-8 text-orange-500 animate-pulse" />
              <div className="absolute -inset-1 bg-orange-500/20 rounded-full blur-sm" />
            </div>
            <div>
              <CardTitle className="text-xl font-bold bg-gradient-to-r from-yellow-400 to-amber-500 bg-clip-text text-transparent">
                Binance High Risk Buys
              </CardTitle>
              <p className="text-xs text-muted-foreground">Under A$50M Market Cap • Binance-listed pairs • CoinGecko market data • High Risk Plays</p>
            </div>
          </div>
          
          <div className="flex items-center gap-2 flex-wrap">
            {analysis?.created_at && (
              <Badge
                variant="outline"
                className="text-xs gap-1 bg-green-500/10 border-green-500/30 text-green-400"
                title={format(new Date(analysis.created_at), 'PPp')}
              >
                <Clock className="w-3 h-3" />
                Live • {formatDistanceToNow(new Date(analysis.created_at), { addSuffix: true })}
              </Badge>
            )}
            <Badge variant="destructive" className="animate-pulse">
              <AlertTriangle className="w-3 h-3 mr-1" />
              EXTREME RISK
            </Badge>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={handleRefresh}
              disabled={isRefreshing}
              className="border-red-500/30 hover:bg-red-500/10"
            >
              <RefreshCw className={`w-4 h-4 mr-1 ${isRefreshing ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>
      </CardHeader>

      <CardContent className="p-6 space-y-6">
        {/* AI Market Analysis Panel */}
        {analysis && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-card/30 rounded-lg p-4 border border-muted">
              <div className="flex items-center gap-2 mb-2">
                <BarChart3 className="w-4 h-4 text-blue-400" />
                <span className="text-sm font-semibold text-foreground">Market Sentiment</span>
              </div>
              <p className="text-sm text-muted-foreground">{analysis.sentiment_analysis}</p>
            </div>

            <div className="bg-card/30 rounded-lg p-4 border border-muted">
              <div className="flex items-center gap-2 mb-2">
                <TrendingUp className="w-4 h-4 text-green-400" />
                <span className="text-sm font-semibold text-foreground">Technical Outlook</span>
              </div>
              <p className="text-sm text-muted-foreground">{analysis.technical_outlook}</p>
            </div>

            <div className="bg-card/30 rounded-lg p-4 border border-muted">
              <div className="flex items-center gap-2 mb-2">
                <Target className="w-4 h-4 text-purple-400" />
                <span className="text-sm font-semibold text-foreground">Catalyst Watch</span>
              </div>
              <p className="text-sm text-muted-foreground">{analysis.catalyst_watch}</p>
            </div>

            <div className="bg-red-950/30 rounded-lg p-4 border border-red-500/30">
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle className="w-4 h-4 text-red-400" />
                <span className="text-sm font-semibold text-red-400">Risk Warning</span>
              </div>
              <p className="text-sm text-red-400/80">{analysis.risk_warning}</p>
            </div>
          </div>
        )}

        {/* Trending Narratives */}
        {analysis?.trending_narratives && analysis.trending_narratives.length > 0 && (
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs text-muted-foreground">Hot Narratives:</span>
            {analysis.trending_narratives.map((narrative, idx) => (
              <Badge key={idx} variant="outline" className="text-xs bg-purple-500/10 border-purple-500/30 text-purple-400">
                <Sparkles className="w-3 h-3 mr-1" />
                {narrative}
              </Badge>
            ))}
          </div>
        )}

        {/* Token Cards */}
        {tokens && tokens.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
            {tokens.map((token, idx) => (
              <TokenCard key={token.id} token={token} rank={idx + 1} />
            ))}
          </div>
        ) : (
          <div className="text-center py-8">
            <Flame className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
            <p className="text-muted-foreground">No low-cap gems found today</p>
            <Button 
              variant="outline" 
              className="mt-4"
              onClick={handleRefresh}
              disabled={isRefreshing}
            >
              Run Analysis
            </Button>
          </div>
        )}

        {/* Risk Disclaimer */}
        <div className="bg-red-950/50 border border-red-500/50 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-6 h-6 text-red-500 flex-shrink-0 mt-0.5" />
            <div>
              <h4 className="font-bold text-red-400 mb-1">Extreme Risk Warning</h4>
              <p className="text-sm text-red-400/80">
                These micro-cap tokens are EXTREMELY HIGH RISK. They can lose 80-100% of their value in hours.
                Many are pump-and-dump schemes. Never invest more than you can afford to lose completely.
              </p>
              <DisclaimerNote className="mt-2 text-red-400/80" />
            </div>
          </div>
        </div>

        {/* Last Updated */}
        {analysis && (
          <p className="text-xs text-muted-foreground text-center">
            Last updated: {format(new Date(analysis.created_at), 'PPp')}
          </p>
        )}
      </CardContent>
    </Card>
  );
};

export default LowCapGemsWidget;
